import { DateTime, Schema } from "effect";
import type { AuditEvent, DocVersion } from "./audit.ts";
import {
  DocumentArchived,
  DocumentCreated,
  DocumentCreatedCollabLite,
  DocumentGenerationCompletedLite,
  DocumentGenerationFailed,
  DocumentPhaseAdvanced,
  DocumentPublished,
  DocumentReworkClosed,
  DocumentReworkOpened,
  DocumentRoleUpdated,
  DocumentStatusUpdated,
  DocumentUnarchived,
  isDocumentModifying,
} from "./audit.ts";
import {
  type CurriculumPhase,
  type CurriculumRole,
  type CurriculumStatus,
  isPhaseAdvance,
  type PhaseTimestamps,
} from "./curriculum-tags.ts";
import type { DocumentId } from "./ids.ts";

// The pure lifecycle fold: one document's ordered audit events in, its current lifecycle out.
//
// Everything an index row needs about *where a curriculum stands* is derived here — which document
// it is (role), how ready its content is (phase), which version learners see (the published pin),
// whether part of it has gone back to an earlier phase (rework), which document it replaced, and the
// chain facts (created / updated / head version). It is deliberately ONE fold rather than a method
// per axis: every consumer wants the whole set together, and a single pass keeps the invariants
// between axes (a rework only exists on published content; the pin outlives later commits) provable
// in one place.
//
// It is also where the RETIRED status axis is reconciled. `document.status-updated` conflated role
// and phase; the audit log is append-only, so those events are never rewritten — they are folded
// onto the two axes on read. That makes the split a pure read-model change: no migration, no
// backfill, no rewritten bytes.

// The event tags the ordered fold actually reads. Exported so a store that can push work to its
// query engine selects exactly these rows (and no review events) rather than re-deriving the list.
export const LIFECYCLE_EVENT_TAGS = [
  "document.created",
  "document.created.v2",
  "document.role-updated",
  "document.phase-advanced",
  "document.published",
  "document.rework-opened",
  "document.rework-closed",
  // the generation-status axis (KTD3): the terminal events a `generating`-origin genesis resolves to
  "document.generation-completed",
  "document.generation-failed",
  // retired, still folded on read
  "document.status-updated",
  "document.archived",
  "document.unarchived",
] as const;

// The decode-time twin of `LIFECYCLE_EVENT_TAGS`: every field `foldLifecycleAxes` reads off these
// tags, minus the two genesis variants' content bytes (`DocumentCreated.patch`,
// `DocumentCreatedCollab.changeSetJson` — see `DocumentCreatedCollabLite`). A store that projects
// these fields in its query engine decodes through this instead of the full `AuditEvent`, so a
// document's initial text never has to leave the store just to learn its role/phase/status.
export const LifecycleEvent = Schema.Union(
  DocumentCreated.pipe(Schema.omit("patch")),
  DocumentCreatedCollabLite,
  DocumentRoleUpdated,
  DocumentPhaseAdvanced,
  DocumentPublished,
  DocumentReworkOpened,
  DocumentReworkClosed,
  DocumentGenerationCompletedLite,
  DocumentGenerationFailed,
  DocumentStatusUpdated,
  DocumentArchived,
  DocumentUnarchived,
);
export type LifecycleEvent = typeof LifecycleEvent.Type;

// The chain facts, which are aggregates rather than an ordered fold — a store with a query engine
// gets them from `MIN(at)`, `MAX(at)` and `MAX(doc_version)` instead of reading events.
export interface CurriculumChainFacts {
  readonly createdAt: DateTime.Utc;
  readonly updatedAt: DateTime.Utc;
  readonly docVersion: DocVersion;
}

// The ordered part: which document this is, how ready it is, what is pinned, what has been re-entered.
export interface CurriculumLifecycleAxes {
  readonly role: CurriculumRole;
  readonly phase: CurriculumPhase;
  // The version learners see. Absent until the document is first published; thereafter it holds
  // through any amount of editing until an Expert publishes again.
  readonly publishedVersion?: DocVersion;
  // Set while a rework is open — the earlier, already-passed phase that was re-entered. The
  // document's own `phase` is untouched throughout, published or not; this is the exception to
  // forward-only, not a phase regression.
  readonly reworkPhase?: CurriculumPhase;
  // Succession: the document this one replaced when it was promoted. Distinct from `clonedFrom`,
  // which records where bytes came from.
  readonly supersedes?: DocumentId;
  // When each phase was ENTERED — so "structure passed" is simply when `exercises` began. Only phases
  // actually reached appear: a document that started at `structure` never entered it as a transition,
  // and an ignored (regressing) advance stamps nothing, because the document never went there.
  readonly phaseEnteredAt?: PhaseTimestamps;
  // When the document FIRST went live. Later releases move the pin, not this date.
  readonly publishedAt?: DateTime.Utc;
  // Derived, never stored state (KTD3): "pending" while a `generating`-origin genesis (KTD1) has
  // no terminal event yet, "failed" once `document.generation-failed` lands, and absent both
  // beforehand (an ordinary genesis) and afterwards (`document.generation-completed` resolved it) —
  // so a caller never has to remember to clear it, the fold just stops asserting it.
  readonly generationStatus?: "pending" | "failed";
  // The `document.generation-failed` event's own `reason`, carried alongside `generationStatus`
  // rather than folded away — a bootstrapping/reconnecting client rebuilds its whole view from this
  // fold (there is no other source once the live WS frame that originally carried the reason has
  // passed), so dropping it here means every viewer who was not watching live never learns why.
  readonly failureReason?: string;
}

export interface CurriculumLifecycle extends CurriculumLifecycleAxes, CurriculumChainFacts {}

// Is there something NEW to release — head strictly ahead of the pin?
//
// Strictly, not merely different. The two numbers come from different readers: the pin is what the
// server resolved as head when it published, while a caller's `docVersion` may be the version its own
// view has caught up to. A head that LAGS the pin is that view being behind, not unreleased work, and
// offering to publish a version older than the live one is never right. Every surface that offers a
// publish asks this one question, so it is answered once here rather than re-derived per component.
export const hasUnreleasedDrift = (
  publishedVersion: number | undefined,
  docVersion: number,
): boolean => publishedVersion !== undefined && docVersion > publishedVersion;

// How a legacy `status` reads on the two new axes.
//
// `latest` meant "the newest line being worked on" and `stable` meant "the released one" — a
// distinction the first cut of this map flattened by sending both to `published`. Verified against the
// real corpus: a `latest` document was mid exercise-review while a `stable` one was live. So `latest`
// lands at `exercises` and only `stable` claims `published`.
//
// `archived` sets the ROLE ALONE (`phase` absent). It says which document to use, not what state its
// content reached; asserting `published` too would invent a release we have no record of. Keeping the
// two independent here is the same orthogonality the split exists for.
const LEGACY_STATUS: Record<CurriculumStatus, { role: CurriculumRole; phase?: CurriculumPhase }> = {
  exploratory: { role: "experiment", phase: "structure" },
  latest: { role: "canonical", phase: "exercises" },
  stable: { role: "canonical", phase: "published" },
  archived: { role: "archived" },
};

// Genesis tags differ across the eras; both carry `at`, and only the collab genesis carries the
// role/phase an import may have set.
const isGenesis = (e: AuditEvent): boolean =>
  e._tag === "document.created" || e._tag === "document.created.v2";

// The ordered fold over lifecycle-relevant events. Total and defaulting: an empty list yields the
// defaults, so a curriculum that has never had a lifecycle event still reads sensibly.
export const foldLifecycleAxes = (
  events: readonly (AuditEvent | LifecycleEvent)[],
): CurriculumLifecycleAxes => {
  let role: CurriculumRole = "canonical";
  let phase: CurriculumPhase = "structure";
  let publishedVersion: DocVersion | undefined;
  let reworkPhase: CurriculumPhase | undefined;
  let supersedes: DocumentId | undefined;
  let publishedAt: DateTime.Utc | undefined;
  let generationStatus: "pending" | "failed" | undefined;
  let failureReason: string | undefined;
  const phaseEnteredAt: { -readonly [K in CurriculumPhase]?: DateTime.Utc } = {};

  // Stamped only where the phase genuinely changes, so a refused or repeated transition leaves no date
  // behind. Every path that moves the phase goes through here.
  const enterPhase = (to: CurriculumPhase, at: DateTime.Utc) => {
    phase = to;
    phaseEnteredAt[to] = at;
  };

  for (const event of events) {
    switch (event._tag) {
      case "document.created.v2": {
        // A document's INITIAL lifecycle rides its genesis (ADR-0066 for `status`, ADR-0083 for the
        // two axes), so all three are read here. The legacy `status` is applied FIRST so an explicit
        // `role`/`phase` on the same event wins — a newer declaration is never overridden by the
        // older one it replaced.
        //
        // Missing this was a real defect: a document whose only status declaration lived on its
        // genesis fell to the defaults, so a `stable` one read as `structure` and an `exploratory` one
        // read as `canonical` — the latter also producing two canonical documents at one level.
        if (event.status !== undefined) {
          const mapped = LEGACY_STATUS[event.status];
          role = mapped.role;
          if (mapped.phase !== undefined && mapped.phase !== phase)
            enterPhase(mapped.phase, event.at);
        }
        if (event.role !== undefined) role = event.role;
        // A phase declared AT genesis is where the document started, not a transition into it — so it
        // is set without a date, the same as the default `structure`.
        if (event.phase !== undefined) phase = event.phase;
        // KTD1/KTD3: a `generating`-origin genesis starts life "pending" until its terminal event
        // (below) resolves it.
        if (event.origin === "generating") generationStatus = "pending";
        break;
      }
      case "document.role-updated": {
        role = event.role;
        if (event.supersedes !== undefined) supersedes = event.supersedes;
        break;
      }
      case "document.phase-advanced": {
        // Forward-only, enforced HERE rather than only at the write edge, so no log — however it was
        // produced or ordered — can present a regressed phase.
        if (isPhaseAdvance(phase, event.phase)) enterPhase(event.phase, event.at);
        break;
      }
      case "document.published": {
        publishedVersion = event.publishedVersion;
        // First release only: a later publish moves the pin, not the date the document went live.
        publishedAt ??= event.at;
        break;
      }
      case "document.rework-opened": {
        // Only a phase already PASSED can be reworked, published or not; a rework naming the current
        // phase or one still ahead is a malformed line and is ignored.
        if (isPhaseAdvance(event.phase, phase)) reworkPhase = event.phase;
        break;
      }
      case "document.rework-closed": {
        reworkPhase = undefined;
        break;
      }
      case "document.generation-completed": {
        // The run produced real content — the placeholder state is over, resolved to "nothing to
        // report" rather than a third status: the caller reads the document like any other.
        generationStatus = undefined;
        failureReason = undefined;
        break;
      }
      case "document.generation-failed": {
        generationStatus = "failed";
        failureReason = event.reason;
        break;
      }
      // ── the retired status axis, folded on read ──
      case "document.status-updated": {
        const mapped = LEGACY_STATUS[event.status];
        role = mapped.role;
        // `archived` carries no phase — it moves the role and leaves content state alone.
        if (mapped.phase !== undefined && mapped.phase !== phase)
          enterPhase(mapped.phase, event.at);
        break;
      }
      case "document.archived": {
        role = "archived";
        break;
      }
      case "document.unarchived": {
        role = "canonical";
        break;
      }
      default:
        break;
    }
  }

  return {
    role,
    phase,
    ...(publishedVersion !== undefined ? { publishedVersion } : {}),
    ...(reworkPhase !== undefined ? { reworkPhase } : {}),
    ...(supersedes !== undefined ? { supersedes } : {}),
    ...(Object.keys(phaseEnteredAt).length > 0 ? { phaseEnteredAt } : {}),
    ...(publishedAt !== undefined ? { publishedAt } : {}),
    ...(generationStatus !== undefined ? { generationStatus } : {}),
    ...(failureReason !== undefined ? { failureReason } : {}),
  };
};

// The chain facts, read off a complete event list. A store that can aggregate in its query engine
// should do that instead and assemble with `foldLifecycleAxes` directly.
//
// `updatedAt` is when the DOCUMENT last changed, not when something last happened to it: only
// byte-moving events advance it (`isDocumentModifying`). Every event used to count, which made the
// index's "Updated" column read as "last touched" — editing tags, advancing a phase or publishing all
// stamped it, so a corpus that had a metadata pass swept over it showed the same date for every row.
// Genesis is itself byte-moving, so `updatedAt >= createdAt` always holds.
export const chainFactsOf = (events: readonly AuditEvent[]): CurriculumChainFacts | undefined => {
  const genesis = events.find(isGenesis);
  if (genesis === undefined) return undefined;
  let updatedAt = genesis.at;
  let docVersion: DocVersion = 0;
  for (const event of events) {
    if (isDocumentModifying(event) && DateTime.greaterThan(event.at, updatedAt))
      updatedAt = event.at;
    const version = "docVersion" in event ? event.docVersion : undefined;
    if (version !== undefined && version > docVersion) docVersion = version;
  }
  return { createdAt: genesis.at, updatedAt, docVersion };
};

// The whole lifecycle from a complete chain — the convenience a store without a query engine uses,
// and the surface the fold's invariants are tested through. `undefined` when the slice has no
// genesis, i.e. it is not a document.
export const foldCurriculumLifecycle = (
  events: readonly AuditEvent[],
): CurriculumLifecycle | undefined => {
  const facts = chainFactsOf(events);
  if (facts === undefined) return undefined;
  return { ...foldLifecycleAxes(events), ...facts };
};
